import ActionButton from "#/components/buttons/ActionButton";
import PermissionTooltip from "#/components/ui/PermissionTooltip";
import { usePermission } from "#/hooks/usePermission";
import { Accordion, TextInput } from "@mantine/core";
import { IconPlus, IconTrash } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";

type CustomFields = Record<string, unknown>;

interface Props {
  fields: CustomFields | null | undefined;
  onChange: (fields: CustomFields) => void;
}

const CustomFieldsAccordion = ({ fields, onChange }: Props) => {
  const { t } = useTranslation();
  const { canWrite, canDelete } = usePermission();

  const entries = Object.entries(fields ?? {});

  const handleAdd = (e: React.MouseEvent) => {
    e.stopPropagation();
    const existing = { ...(fields ?? {}) };
    let newKey = `field_${entries.length + 1}`;
    while (newKey in existing) {
      newKey += "_";
    }
    onChange({ ...existing, [newKey]: "" });
  };

  return (
    <Accordion.Item value="custom_fields">
      <Accordion.Control>
        <div className="flex items-center gap-2">
          <span>{t("common.customFields")}</span>
          <PermissionTooltip disabled={!canWrite()}>
            <ActionButton
              color="black"
              size="xs"
              icon={IconPlus}
              disabled={!canWrite()}
              onClick={handleAdd}
            />
          </PermissionTooltip>
        </div>
      </Accordion.Control>
      <Accordion.Panel>
        {entries.length === 0 ? (
          <div className="text-sm text-gray-500">
            {t("common.addCustomFieldHint")}
          </div>
        ) : (
          <div className="w-full flex flex-col gap-2">
            {entries.map(([key, value], index) => (
              <div
                key={`${key}_${index}`}
                className="w-full grid grid-cols-[auto_1fr_1fr_auto] gap-2 items-center"
              >
                <span className="bg-black/5 font-bold text-black/70 rounded-full p-2 w-8 h-8 flex items-center justify-center">
                  <span>{index + 1}</span>
                </span>

                <TextInput
                  defaultValue={key}
                  disabled={!canWrite()}
                  onBlur={(event) => {
                    const newKey = event.currentTarget.value.trim();
                    if (!newKey || newKey === key) return;
                    const updated = { ...(fields ?? {}) };
                    if (newKey in updated) return;
                    const v = updated[key];
                    delete updated[key];
                    updated[newKey] = v;
                    onChange(updated);
                  }}
                />

                <TextInput
                  value={
                    typeof value === "string"
                      ? value
                      : value == null
                        ? ""
                        : JSON.stringify(value)
                  }
                  disabled={!canWrite()}
                  onChange={(event) => {
                    const updated = { ...(fields ?? {}) };
                    updated[key] = event.currentTarget.value;
                    onChange(updated);
                  }}
                />

                <PermissionTooltip disabled={!canDelete()}>
                  <ActionButton
                    icon={IconTrash}
                    variant="white"
                    color="red.5"
                    disabled={!canDelete()}
                    onClick={() => {
                      const updated = { ...(fields ?? {}) };
                      delete updated[key];
                      onChange(updated);
                    }}
                  />
                </PermissionTooltip>
              </div>
            ))}
          </div>
        )}
      </Accordion.Panel>
    </Accordion.Item>
  );
};

export default CustomFieldsAccordion;
